import {
  ConversationDto,
  ConversationOccurrenceDto,
  RecurrenceType,
} from "@coaching-culture/types";
import {
  addWeeks,
  format,
  fromUnixTime,
  isSameMonth,
  isToday,
  startOfToday,
  startOfWeek,
} from "date-fns";
import { groupBy, sortBy } from "lodash";

/** One occurrence, with its series' details alongside. */
export type ConvoAndInstance = ConversationDto & ConversationOccurrenceDto;

export const toInstances = (convos: ConversationDto[]): ConvoAndInstance[] =>
  convos.flatMap((x) =>
    x.occurences.map((o) => ({
      ...x,
      ...o,
      cancelledOn: x.cancelledOn ?? o.cancelledOn,
      cancellationReason: x.cancellationReason ?? o.cancellationReason,
    })),
  );

export const isUpcoming = (convo: { scheduledDate: number }) =>
  startOfToday() < fromUnixTime(convo.scheduledDate);

export const isCancelled = (x: ConvoAndInstance) => x.cancelledOn != null;
export const isCompleted = (x: ConvoAndInstance) => x.completedOn != null;

/** Past, not completed and not cancelled. */
export const needsCompleting = (x: ConvoAndInstance) =>
  !isUpcoming(x) && !isToday(fromUnixTime(x.scheduledDate)) &&
  !isCompleted(x) &&
  !isCancelled(x);

export const playHref = (x: { id: string; index: number }) =>
  `/solutions/performance/conversations/${x.id}/${x.index}/play`;

export const detailsHref = (x: { id: string; index: number }) =>
  `/solutions/performance/conversations/${x.id}/${x.index}`;

const everyN: Record<string, string> = {
  "2": "Every 2",
  "3": "Every 3",
  "4": "Every 4",
  "6": "Every 6",
};

/** Short label: "Weekly", "Every 2 weeks", "Monthly", "Yearly". */
export const recurrenceLabel = (r: RecurrenceType): string | null => {
  if (r === "none") return null;
  if (r === "weekly") return "Weekly";
  if (r === "monthly") return "Monthly";
  if (r === "yearly") return "Yearly";
  const n = everyN[r.charAt(0)];
  if (r.endsWith("weekly")) return `${n} weeks`;
  if (r.endsWith("monthly")) return `${n} months`;
  return null;
};

export type ConvoGroup = { label: string; items: ConvoAndInstance[] };

const weekStart = (d: Date) => startOfWeek(d, { weekStartsOn: 1 });

/**
 * Upcoming: Today, This week, Next week, then "Later in <month>" for the
 * rest of this month and "<Month yyyy>" after that.
 */
const upcomingLabel = (d: Date) => {
  const now = new Date();
  if (isToday(d)) return "Today";
  const ws = weekStart(d).getTime();
  if (ws === weekStart(now).getTime()) return "This week";
  if (ws === weekStart(addWeeks(now, 1)).getTime()) return "Next week";
  if (isSameMonth(d, now)) return `Later in ${format(d, "MMMM")}`;
  return format(d, "MMMM yyyy");
};

export const groupUpcoming = (items: ConvoAndInstance[]): ConvoGroup[] => {
  const sorted = sortBy(items, (x) => x.scheduledDate);
  const groups = groupBy(sorted, (x) =>
    upcomingLabel(fromUnixTime(x.scheduledDate)),
  );
  return sortBy(Object.entries(groups), (x) => x[1][0].scheduledDate).map(
    ([label, items]) => ({ label, items }),
  );
};

/** Past: newest first, by month. */
export const groupPast = (items: ConvoAndInstance[]): ConvoGroup[] => {
  const sorted = sortBy(items, (x) => -x.scheduledDate);
  const groups = groupBy(sorted, (x) =>
    format(fromUnixTime(x.scheduledDate), "MMMM yyyy"),
  );
  return sortBy(Object.entries(groups), (x) => -x[1][0].scheduledDate).map(
    ([label, items]) => ({ label, items }),
  );
};
